import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Sumo {
  id: string;
  title: string;
  description: string;
  price: string;
  features: string[];
  specifications: any;
  is_visible: boolean;
  created_at: string;
  updated_at: string;
}

export const useSumos = () => {
  const [sumos, setSumos] = useState<Sumo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSumos = async (includeHidden = false) => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('sumos')
        .select('*');

      if (!includeHidden) {
        query = query.eq('is_visible', true);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      setSumos(data || []);
      setError(null);
    } catch (err) {
      console.error('Erreur lors du chargement des sumos:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      toast.error('Erreur lors du chargement des sumos');
    } finally {
      setLoading(false);
    }
  };

  const createSumo = async (sumoData: Omit<Sumo, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('sumos')
        .insert(sumoData)
        .select()
        .single();

      if (error) {
        throw error;
      }

      setSumos(prev => [...prev, data]);
      toast.success('Sumo créé avec succès');
      return data;
    } catch (err) {
      console.error('Erreur lors de la création du sumo:', err);
      toast.error('Erreur lors de la création du sumo');
      throw err;
    }
  };

  const updateSumo = async (id: string, updates: Partial<Sumo>) => {
    try {
      const { data, error } = await supabase
        .from('sumos')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      setSumos(prev => prev.map(sumo => 
        sumo.id === id ? data : sumo
      ));
      toast.success('Sumo mis à jour avec succès');
      return data;
    } catch (err) {
      console.error('Erreur lors de la mise à jour du sumo:', err);
      toast.error('Erreur lors de la mise à jour du sumo');
      throw err;
    }
  };

  const deleteSumo = async (id: string) => {
    try {
      const { error } = await supabase
        .from('sumos')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      setSumos(prev => prev.filter(sumo => sumo.id !== id));
      toast.success('Sumo supprimé avec succès');
    } catch (err) {
      console.error('Erreur lors de la suppression du sumo:', err);
      toast.error('Erreur lors de la suppression du sumo');
      throw err;
    }
  };

  useEffect(() => {
    fetchSumos();
  }, []);

  return {
    sumos,
    loading,
    error,
    fetchSumos,
    createSumo,
    updateSumo,
    deleteSumo,
    refetch: fetchSumos
  };
};